import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import { EmojiEvents, Star, Celebration, WorkspacePremium, Title, Person } from '@mui/icons-material';
import PremiumAchievementNotification from '../Achievements/PremiumAchievementNotification';
import { useUserTitle } from '../../hooks/useUserTitle';
import { useAuth } from '../../contexts/AuthContext';

export default function AchievementTest() {
  const [showNotification, setShowNotification] = useState(false);
  const [testAchievement, setTestAchievement] = useState({
    name: 'First Steps',
    description: 'Welcome to HypeCircle! You\'ve taken your first step into the world of movie discovery.',
    points: 100,
    rarity: 'common',
    category: 'onboarding'
  });
  const [titleTestUserId, setTitleTestUserId] = useState('');
  const [titleUpdateResult, setTitleUpdateResult] = useState(null);
  
  const { user } = useAuth();
  const { updateTitle, loading: titleLoading, error: titleError } = useUserTitle();

  const sampleAchievements = [
    {
      name: 'First Steps',
      description: 'Welcome to HypeCircle! You\'ve taken your first step into the world of movie discovery.',
      points: 100,
      rarity: 'common',
      category: 'onboarding'
    },
    {
      name: 'Movie Buff',
      description: 'You\'ve watched 50 movies! Your passion for cinema is truly impressive.',
      points: 500,
      rarity: 'rare',
      category: 'watching'
    },
    {
      name: 'Social Butterfly',
      description: 'You\'ve made 100 friends on HypeCircle! Your social network is growing strong.',
      points: 300,
      rarity: 'uncommon',
      category: 'social'
    },
    {
      name: 'Legendary Critic',
      description: 'You\'ve written 500 reviews! Your insights help the entire HypeCircle community.',
      points: 1000,
      rarity: 'legendary',
      category: 'reviews'
    },
    {
      name: 'Hype Master',
      description: 'You\'ve discovered 10 movies before they became trending! You have an eye for hidden gems.',
      points: 750,
      rarity: 'epic',
      category: 'discovery'
    },
    {
      name: 'Streak Champion',
      description: 'You\'ve logged in for 30 consecutive days! Your dedication is unmatched.',
      points: 400,
      rarity: 'rare',
      category: 'engagement'
    }
  ];

  const handleTestAchievement = (achievement) => {
    setTestAchievement(achievement);
    setShowNotification(true);
  };

  const handleCustomTest = () => {
    setShowNotification(true);
  };

  const handleTitleUpdate = async () => {
    const userId = titleTestUserId || user?.id;
    if (!userId) {
      alert('Please enter a user ID or make sure you are logged in');
      return;
    }

    const result = await updateTitle(userId);
    setTitleUpdateResult(result);
  };

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common': return '#757575';
      case 'uncommon': return '#4CAF50';
      case 'rare': return '#2196F3';
      case 'epic': return '#9C27B0';
      case 'legendary': return '#FFD700';
      case 'mythic': return '#FF4081';
      default: return '#757575';
    }
  };

  const getRarityIcon = (rarity) => {
    switch (rarity) {
      case 'common': return <Star />;
      case 'uncommon': return <EmojiEvents />;
      case 'rare': return <WorkspacePremium />;
      case 'epic': return <Celebration />;
      case 'legendary': return <EmojiEvents />;
      default: return <Star />;
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        Achievement Notification Test
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        This is an admin-only testing interface for achievement notifications. 
        Test different achievement types and see how they appear to users.
      </Alert>

      {/* Custom Achievement Test */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Custom Achievement Test
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Achievement Name"
                value={testAchievement.name}
                onChange={(e) => setTestAchievement(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Points"
                type="number"
                value={testAchievement.points}
                onChange={(e) => setTestAchievement(prev => ({ ...prev, points: parseInt(e.target.value) || 0 }))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Rarity</InputLabel>
                <Select
                  value={testAchievement.rarity}
                  label="Rarity"
                  onChange={(e) => setTestAchievement(prev => ({ ...prev, rarity: e.target.value }))}
                >
                  <MenuItem value="common">Common</MenuItem>
                  <MenuItem value="uncommon">Uncommon</MenuItem>
                  <MenuItem value="rare">Rare</MenuItem>
                  <MenuItem value="epic">Epic</MenuItem>
                  <MenuItem value="legendary">Legendary</MenuItem>
                  <MenuItem value="mythic">Mythic</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={testAchievement.description}
                onChange={(e) => setTestAchievement(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
          </Grid>
          <Button
            variant="contained"
            onClick={handleCustomTest}
            startIcon={<EmojiEvents />}
            size="large"
          >
            Test Custom Achievement
          </Button>
        </CardContent>
      </Card>

      {/* Sample Achievements */}
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Sample Achievement Tests
      </Typography>
      
      <Grid container spacing={2}>
        {sampleAchievements.map((achievement, index) => (
          <Grid item xs={12} md={6} lg={4} key={index}>
            <Card 
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                }
              }}
              onClick={() => handleTestAchievement(achievement)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ 
                    color: getRarityColor(achievement.rarity),
                    mr: 1 
                  }}>
                    {getRarityIcon(achievement.rarity)}
                  </Box>
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    {achievement.name}
                  </Typography>
                  <Chip
                    label={achievement.rarity}
                    size="small"
                    sx={{
                      backgroundColor: getRarityColor(achievement.rarity),
                      color: 'white',
                      textTransform: 'capitalize'
                    }}
                  />
                </Box>
                
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ mb: 2, minHeight: 40 }}
                >
                  {achievement.description}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip
                    label={achievement.category}
                    variant="outlined"
                    size="small"
                  />
                  <Typography variant="body2" fontWeight="bold">
                    +{achievement.points} pts
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Title Update Test */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Title />
            Title Update Test
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            Test the title update system. This will check the user's achievements and update their title based on the TitleDefinition table.
          </Alert>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="User ID (leave empty to use current user)"
                value={titleTestUserId}
                onChange={(e) => setTitleTestUserId(e.target.value)}
                placeholder={user?.id || 'Enter user ID'}
                helperText={user?.id ? `Current user: ${user.id}` : 'No user logged in'}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleTitleUpdate}
                disabled={titleLoading}
                startIcon={titleLoading ? <CircularProgress size={20} /> : <Person />}
                size="large"
                sx={{ height: '56px' }}
              >
                {titleLoading ? 'Updating...' : 'Update Title'}
              </Button>
            </Grid>
          </Grid>

          {titleError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error: {titleError}
            </Alert>
          )}

          {titleUpdateResult && (
            <Alert 
              severity={titleUpdateResult.success ? 'success' : 'error'} 
              sx={{ mb: 2 }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Result:</strong> {titleUpdateResult.message}
              </Typography>
              
              {titleUpdateResult.success && (
                <Box>
                  <Typography variant="body2">
                    <strong>Title Changed:</strong> {titleUpdateResult.titleChanged ? 'Yes' : 'No'}
                  </Typography>
                  
                  {titleUpdateResult.titleChanged && (
                    <>
                      <Typography variant="body2">
                        <strong>Previous Title:</strong> {titleUpdateResult.previousTitle}
                      </Typography>
                      <Typography variant="body2">
                        <strong>New Title:</strong> {titleUpdateResult.titleName || titleUpdateResult.newTitle}
                      </Typography>
                      {titleUpdateResult.titleDescription && (
                        <Typography variant="body2">
                          <strong>Description:</strong> {titleUpdateResult.titleDescription}
                        </Typography>
                      )}
                    </>
                  )}
                  
                  <Typography variant="body2">
                    <strong>Achievement Count:</strong> {titleUpdateResult.achievementCount}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total Points:</strong> {titleUpdateResult.totalPoints}
                  </Typography>
                </Box>
              )}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Achievement Notification */}
      <PremiumAchievementNotification
        achievement={testAchievement}
        show={showNotification}
        onClose={() => setShowNotification(false)}
      />
    </Box>
  );
}